import { useEffect, useRef, useState } from 'react';
import useElementSizeObserver from '../../hooks/useElementSizeObserver';

/* the animation is authored against this fixed reference box, then the
whole stage is CSS-scaled (cover-style) to fill whatever size the
container actually renders at */
const REF_WIDTH = 360;
const REF_HEIGHT = 560;

const BALLOON_START_X = 180;
const BALLOON_START_Y = 580;
const BALLOON_TARGET_Y = 120;

const FLOAT_FRAMES = 176;
const POP_FRAMES = 22;
const BURST_FRAMES = 130;
const RESET_FRAMES = 40;

export const BALLOON_FILL = '#b4be9c';
export const BALLOON_STROKE = '#2b3a52';

const POP_LINE_COLOR = '#8a9670';
const FRAGMENT_SHADES = [
	'#243d32',
	'#5b696f',
	'#b4be9c',
	'#b6d3e2'
];

type Phase = 'float' | 'pop' | 'burst' | 'reset' | 'done';

type Fragment = {
	x: number;
	y: number;
	vx: number;
	vy: number;
	gravity: number;
	angle: number;
	spin: number;
	cpx1: number;
	cpy1: number;
	cpx2: number;
	cpy2: number;
	ex: number;
	ey: number;
	width: number;
	opacity: number;
	fade: number;
	color: string;
};

const randomBetween = ( min: number, max: number ) =>
	min + Math.random() * ( max - min );

const createFragments = ( x: number, y: number ): Fragment[] =>
	Array.from( { length: 20 }, () => {
		const angle = randomBetween( 0, Math.PI * 2 );
		const speed = randomBetween( 3, 10 );
		const len = randomBetween( 6, 16 );
		const curve = randomBetween( -8, 8 );
		return {
			x,
			y,
			vx: Math.cos( angle ) * speed,
			vy: Math.sin( angle ) * speed - randomBetween( 1, 5 ),
			gravity: randomBetween( 0.15, 0.3 ),
			angle: randomBetween( 0, Math.PI * 2 ),
			spin: randomBetween( -0.13, 0.13 ),
			cpx1: len * 0.25,
			cpy1: curve,
			cpx2: len * 0.75,
			cpy2: curve * 0.7,
			ex: len,
			ey: randomBetween( -4, 4 ),
			width: randomBetween( 2, 4 ),
			opacity: 1,
			fade: randomBetween( 0.006, 0.014 ),
			color:
				FRAGMENT_SHADES[ Math.floor( Math.random() * FRAGMENT_SHADES.length ) ],
		};
	} );

const drawFragments = (
	ctx: CanvasRenderingContext2D,
	fragments: Fragment[]
) => {
	for ( const fragment of fragments ) {
		fragment.vy += fragment.gravity;
		fragment.x += fragment.vx;
		fragment.y += fragment.vy;
		fragment.vx *= 0.985;
		fragment.angle += fragment.spin;
		fragment.opacity = Math.max( 0, fragment.opacity - fragment.fade );

		ctx.save();
		ctx.globalAlpha = fragment.opacity;
		ctx.translate( fragment.x, fragment.y );
		ctx.rotate( fragment.angle );
		ctx.beginPath();
		ctx.moveTo( 0, 0 );
		ctx.bezierCurveTo(
			fragment.cpx1,
			fragment.cpy1,
			fragment.cpx2,
			fragment.cpy2,
			fragment.ex,
			fragment.ey
		);
		ctx.strokeStyle = fragment.color;
		ctx.lineWidth = fragment.width;
		ctx.lineCap = 'round';
		ctx.stroke();
		ctx.restore();
	}
};

export const useBalloonPops = ( looping: boolean ) => {
	const {
		ref: containerRef,
		width,
		height,
	} = useElementSizeObserver<HTMLDivElement>();
	const canvasRef = useRef<HTMLCanvasElement>( null );
	const frameRef = useRef( 0 );
	const rafRef = useRef<number>( null );

	const [ balloonPos, setBalloonPos ] = useState( {
		x: BALLOON_START_X,
		y: BALLOON_START_Y,
	} );
	const [ visible, setVisible ] = useState( true );

	const stateRef = useRef( {
		phase: 'float' as Phase,
		t: 0,
		balloonX: BALLOON_START_X,
		balloonY: BALLOON_START_Y,
		fragments: [] as Fragment[],
		popLines: [] as { angle: number; len: number }[],
		popAlpha: 1,
	} );

	useEffect( () => {
		const canvas = canvasRef.current;
		if ( !canvas ) {
			return;
		}
		const ctx = canvas.getContext( '2d' );
		if ( !ctx ) {
			return;
		}
		const s = stateRef.current;

		const loop = () => {
			ctx.clearRect( 0, 0, canvas.width, canvas.height );
			const frame = frameRef.current;
			const sway = Math.sin( frame * 0.045 ) * 7;

			if ( s.phase === 'float' ) {
				const p = Math.min( s.t / FLOAT_FRAMES, 1 );
				const ease = 1 - ( 1 - p ) ** 2.5;
				s.balloonY =
					BALLOON_START_Y + ( BALLOON_TARGET_Y - BALLOON_START_Y ) * ease;
				s.balloonX = BALLOON_START_X + sway;
				setBalloonPos( { x: s.balloonX, y: s.balloonY } );
				setVisible( true );
				s.t++;
				if ( s.t >= FLOAT_FRAMES ) {
					s.phase = 'pop';
					s.t = 0;
					s.fragments = createFragments( s.balloonX, s.balloonY );
					s.popLines = Array.from( { length: 10 }, ( _, i ) => ( {
						angle: ( i / 10 ) * Math.PI * 2 + randomBetween( -0.2, 0.2 ),
						len: randomBetween( 20, 44 ),
					} ) );
					s.popAlpha = 1;
					setVisible( false );
				}
			} else if ( s.phase === 'pop' ) {
				s.popAlpha = 1 - s.t / POP_FRAMES;
				ctx.save();
				ctx.globalAlpha = s.popAlpha * 0.65;
				for ( const { angle, len } of s.popLines ) {
					ctx.beginPath();
					ctx.moveTo(
						s.balloonX + Math.cos( angle ) * 10,
						s.balloonY + Math.sin( angle ) * 10
					);
					ctx.lineTo(
						s.balloonX + Math.cos( angle ) * len,
						s.balloonY + Math.sin( angle ) * len
					);
					ctx.strokeStyle = POP_LINE_COLOR;
					ctx.lineWidth = randomBetween( 1.5, 3 );
					ctx.lineCap = 'round';
					ctx.stroke();
				}
				ctx.restore();
				drawFragments( ctx, s.fragments );
				s.t++;
				if ( s.t >= POP_FRAMES ) {
					s.phase = 'burst';
					s.t = 0;
				}
			} else if ( s.phase === 'burst' ) {
				drawFragments( ctx, s.fragments );
				s.t++;
				if ( s.t >= BURST_FRAMES ) {
					s.phase = looping ? 'reset' : 'done';
					s.t = 0;
				}
			} else if ( s.phase === 'reset' ) {
				s.t++;
				if ( s.t >= RESET_FRAMES ) {
					s.phase = 'float';
					s.t = 0;
					s.balloonY = BALLOON_START_Y;
					s.fragments = [];
					setVisible( true );
					setBalloonPos( { x: BALLOON_START_X, y: BALLOON_START_Y } );
				}
			}

			if ( s.phase === 'done' ) {
				return;
			}

			frameRef.current++;
			rafRef.current = requestAnimationFrame( loop );
		};

		rafRef.current = requestAnimationFrame( loop );
		return () => {
			if ( rafRef.current ) {
				cancelAnimationFrame( rafRef.current );
			}
		};
	}, [ looping ] );

	const frame = frameRef.current;
	const sway = Math.sin( frame * 0.045 ) * 7;
	const knotX = balloonPos.x;
	const knotY = balloonPos.y + 72;
	const stringEndY = knotY + 160;

	const scale =
		width && height ? Math.max( width / REF_WIDTH, height / REF_HEIGHT ) : 1;

	return {
		containerRef,
		canvasRef,
		visible,
		scale,
		stageWidth: REF_WIDTH,
		stageHeight: REF_HEIGHT,
		balloonX: balloonPos.x,
		balloonY: balloonPos.y,
		knotX,
		knotY,
		stringEndY,
		sway,
	};
};
